import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { PayerProductStatus, PayerStatus } from '../types';

import { IntakeStatusChip } from './IntakeStatusChip';

interface PayerStatusCardProps {
  payer: PayerStatus;
}

const STATUS_TONE: Record<PayerProductStatus, 'default' | 'warning' | 'success' | 'primary'> = {
  not_started: 'default',
  needs_info: 'warning',
  in_progress: 'warning',
  submitted: 'primary',
  approved: 'success',
  active: 'success',
};

export function PayerStatusCard({ payer }: PayerStatusCardProps) {
  const theme = useTheme();
  const tone = STATUS_TONE[payer.status];
  const accent =
    tone === 'success'
      ? theme.palette.success.main
      : tone === 'warning'
        ? theme.palette.warning.main
        : tone === 'primary'
          ? theme.palette.primary.main
          : theme.palette.grey[400];

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.75,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        borderLeft: `3px solid ${accent}`,
        minHeight: 132,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 850 }}>
            {payer.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {payer.effectiveDate ? `Effective ${payer.effectiveDate}` : payer.note}
          </Typography>
        </Box>
        <IntakeStatusChip status={payer.status} />
      </Stack>

      {payer.effectiveDate && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {payer.note}
        </Typography>
      )}

      {payer.warning && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 1,
            p: 0.9,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.dark,
          }}
        >
          <WarningAmberOutlinedIcon sx={{ fontSize: 18, mt: 0.1 }} />
          <Typography variant="caption" sx={{ fontWeight: 650 }}>
            {payer.warning}
          </Typography>
        </Stack>
      )}

      {payer.requiredItems && payer.requiredItems.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            Needed
          </Typography>
          <Stack spacing={0.55} sx={{ mt: 0.5 }}>
            {payer.requiredItems.map(item => (
              <Stack
                key={item.id}
                direction="row"
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Typography variant="caption" sx={{ fontWeight: 650 }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 850 }}>
                  {item.actionLabel ?? 'Open'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
